import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { site } from "@/config/site";
import { Seo } from "@/components/Seo";
import { CtaBand } from "@/components/CtaBand";
import { TeachingAffiliation } from "@/features/teaching/catalog";

const timeline: { period: string; fact: ReactNode }[] = [
  {
    period: "1999–2001",
    fact: "University of the Witwatersrand. PhD completed 2001, and Carnegie Mellon — Product Line Practice and the CASOS Center. Papers on product lines and learning theory.",
  },
  {
    period: "2003",
    fact: "Process Innovation Imperative first published.",
  },
  {
    period: "2008",
    fact: "IEEE paper on software product-line adoption in large banks.",
  },
  {
    period: "2011",
    fact: "Built to Thrive. Eric Hoffer Award listing follows.",
  },
  {
    period: "Mid-2010s",
    fact: "Innovation for Jobs (i4j) — analytics co-chair; ecosystem graph of the jobs-innovation landscape.",
  },
  {
    period: "2018",
    fact: "FinovateFall New York — Client Pulse Responder and AutoML platform.",
  },
  {
    period: "2022",
    fact: "Public SaaS launch at AI Africa Expo.",
  },
  {
    period: "2023",
    fact: "Human behavioral algorithms — conference paper, Singapore.",
  },
  {
    period: "2026",
    fact: "The Human Organism as Algorithm — current research book.",
  },
  {
    period: "Now",
    fact: (
      <>
        Founder & Head of AI Platform,{" "}
        <a href={site.profiles.ecosystem} rel="noreferrer noopener">
          ecosystem.Ai
        </a>{" "}
        and{" "}
        <a href={site.profiles.ecosystemCode} rel="noreferrer noopener">
          EcosystemCode
        </a>
        . Teaching Associate at{" "}
        <a href="https://www.henley.ac.uk/" rel="noreferrer noopener">
          Henley Business School
        </a>{" "}
        and{" "}
        <a href="https://www.sun.ac.za/" rel="noreferrer noopener">
          Stellenbosch University
        </a>
        . Based between San Francisco and Cape Town.
      </>
    ),
  },
];

export function AboutPage() {
  return (
    <>
      <Seo
        title="About"
        description="Jay van Zyl is founder and Head of AI Platform at ecosystem.Ai. Computational social scientist, product-line practitioner, author."
        path="/about"
      />
      <section className="wrap hero">
        <div>
          <p className="eyebrow">About</p>
          <h1>Research, digital, and intelligent technology</h1>
          <p className="lede">
            Predictive ways to engage people in real time. Personalization and the role of
            AI. The changing world for the individual, the collective, and society, and how
            AI contributes to success.
          </p>
        </div>
        <figure className="portrait">
          <img
            src={site.portraits.primary.src}
            alt={site.portraits.primary.alt}
            width={640}
            height={800}
          />
        </figure>
      </section>
      <section className="wrap">
        <div className="prose">
        <p>
          The first half of the career was about the <em>how</em> of innovation: product
          lines, capability models, and the systems that take an idea into a business. That
          work led to a PhD in 2001 at the University of the Witwatersrand and Carnegie Mellon
          with Product Line Practice and the CASOS Center (Center for
          Computational Analysis of Social and Organizational Systems). The academic papers
          from that period are on product lines and learning theory. <em>Process Innovation
          Imperative</em> came out of the same line. Digital technology then shifted the
          question to <em>why</em> people innovate, and how they integrate with their
          environment. <em>Built to Thrive</em> (2011) put social science and technology in
          the same conversation.
        </p>
        <p>
          The latest book is <em>The Human Organism as Algorithm</em>. The
          informational environment in which decisions are made is increasingly
          networked, mediated, and optimized by systems we do not control.
        </p>
        <p>
          The current work is two platforms under Ecogenetic LLC. ecosystem.Ai puts
          behavioral prediction into production: workbench, prediction server, pulse
          runtime, and data agents. EcosystemCode makes architecture executable: a
          modeling workbench and a governed ontology become a deterministic running
          system. Same idea, two products — prediction in the interaction, and design
          that does not evaporate between the diagram and the code.
        </p>
        <p>
          The practice that produces both is one lifecycle: a prediction story,
          a spec and an ontology, agents, code, deployment, and a system that
          has to stay reliable after it ships.{" "}
          <Link to="/building">How the software gets built</Link>.
        </p>
        <p>
          <TeachingAffiliation />{" "}
          <Link to="/teaching">The courses</Link> are that same argument, taught in
          the room.
        </p>
        <p>
          I am interested in mega-waves of change: how humans remake themselves through
          cycles of innovation and intelligence, and how platforms emerge long after a
          breakthrough.
        </p>
        </div>
      </section>
      <section className="wrap section">
        <p className="section-kicker">Path</p>
        <ol className="timeline">
          {timeline.map((item) => (
            <li key={item.period}>
              <p className="meta">{item.period}</p>
              <p>{item.fact}</p>
            </li>
          ))}
        </ol>
        <p className="meta">
          Earlier company names and founding-year variants are held back until
          confirmed. Academic profiles:{" "}
          <a href={site.profiles.academia}>Academia.edu</a> ·{" "}
          <a href={site.profiles.researchgate}>ResearchGate</a>.
        </p>
      </section>
      <CtaBand />
    </>
  );
}
